import type { ListAndImageProps as Props } from './types';
import { withChildren } from '@builder.io/react';
import { useColorProvider } from '@/utils/ColorProvider';
import { TextAlignmentOptions, HeroImagePlacements, BgImageAlignmentOptions } from '@/constants';
import RichText from '@/components/RichText';
import { cls } from '@/utils/cls';
import style from './ListAndImage.module.scss';
import Image from 'next/image';
export function ListAndImage({
  extendStyles,
  heading,
  headingAlignment = TextAlignmentOptions.LEFT,
  items,
  image,
  imageWidth = 100,
  imageAlignment = TextAlignmentOptions.LEFT,
  imageVerticalAlignment = BgImageAlignmentOptions.TOP,
  color,
  line,
  children,
  textSide = HeroImagePlacements.LEFT,
  mobileImage = true,
  headingSpan = false
}: Props) {
  const _className = cls(style.root, extendStyles);
  const {
    colors
  } = useColorProvider();
  const textLeft = textSide === TextAlignmentOptions.LEFT;
  const textStyles = cls(style.text, textLeft ? style.left : style.right);

  // Create horizontal alignment class for the image
  const getHorizontalAlignmentClass = () => {
    switch (imageAlignment) {
      case TextAlignmentOptions.CENTER:
        return style.alignCenter;
      case TextAlignmentOptions.RIGHT:
        return style.alignRight;
      case TextAlignmentOptions.LEFT:
      default:
        return style.alignLeft;
    }
  };

  // Create vertical alignment class for the image
  const getVerticalAlignmentClass = () => {
    switch (imageVerticalAlignment) {
      case BgImageAlignmentOptions.CENTER:
        return style.alignVerticalCenter;
      case BgImageAlignmentOptions.BOTTOM:
        return style.alignVerticalBottom;
      case BgImageAlignmentOptions.TOP:
      default:
        return style.alignVerticalTop;
    }
  };
  const imageStyles = cls(style.wrapper, textLeft ? style.right : style.left, headingSpan ? style.square : '', getHorizontalAlignmentClass(), getVerticalAlignmentClass());
  const wrapperStyles = cls(style.mobileWrapper, !mobileImage ? style.mobileHide : '', headingSpan ? style.square : '');

  // Data inconsistency from builder, image prop first is a string and then is an object.
  const imageSrc = typeof image === 'string' ? image : image?.src;
  return <section className={_className} style={{
    backgroundColor: color || '#F9FAFB'
  }} data-sentry-component="ListAndImage" data-sentry-source-file="ListAndImage.tsx">
			<div className={style.content} data-grid>
				{heading && headingSpan && <RichText text={heading} className={cls(style.heading, !!line ? style.hr : '')} style={{
        borderColor: colors.secondaryAccentColor
      }} data-align={headingAlignment} />}
				<div className={textStyles}>
					{imageSrc && <div className={wrapperStyles}>
							<Image className={style.image} src={imageSrc} alt="" role="presentation" width={600} height={400} />
						</div>}
					{heading && !headingSpan && <RichText text={heading} className={!!line ? style.hr : ''} style={{
          borderColor: colors.secondaryAccentColor
        }} data-align={headingAlignment} />}
					<div className={style.list}>
						{items.map((item, index) => <div key={index} className={style.listItem}>
								{item.icon && <Image src={item.icon} alt="" role="presentation" className={style.icon} width={64} height={64} />}
								<div className={style.itemContent}>
									<h3 className={style.itemHeading}>{item.heading}</h3>
									{item.subheading && <p className={style.itemSubheading}>{item.subheading}</p>}
								</div>
							</div>)}
					</div>
					{children}
				</div>

				{imageSrc && <div className={imageStyles} style={{
        '--image-width': `${imageWidth}%`
      } as React.CSSProperties}>
						<Image className={style.image} src={imageSrc} alt="" role="presentation" width={600} height={400} />
					</div>}
			</div>
		</section>;
}
export default withChildren(ListAndImage);